/* =========================================================================
   Server-served game flow — PURE logic + injected seams (no DOM, no direct
   api import, so this module is unit-testable and can never form an import
   cycle with api.ts).

   Phase 1 of PLAN-progression.md turns on the infrastructure that already
   existed but was dead: the client now plays the questions the SERVER serves
   (/api/game/round-questions), posts every answer (/api/game/answer → finally
   populates app.UserQuestionHistory, which is what ends the silent repeats),
   and saves + banks XP at every round boundary (/api/game/round-complete).

   FALLBACK LADDER — the bundled packs never stop working:
     1. guest, or no server session      → bundled pack questions (today's game)
     2. endpoint 404 / 503 / offline     → bundled pack questions
     3. slow server (> timeoutMs)        → bundled pack questions
     4. empty or malformed payload       → bundled pack questions
   A fallback is silent and seamless: the round starts on time either way.
   ========================================================================= */
import { HARD_START_CENTS, type Question } from './engine';
import { stableCodeFor } from './data/questionCodes';
import type { ApiResult } from './api';

/* ----------------------------- server DTOs ----------------------------- */

/** One row of POST /api/game/round-questions (camel-cased QuestionDto).
    `level` is the DB vocabulary (0..3); the client's is `hardness` (1..3). */
export interface ServedQuestionDto {
  stableCode: string;
  category: string;
  body: string;
  options: string[];
  correctIndex: number;
  citation: string | null;
  level: number;
  /** ⭐ The round's one hard-mode taste (PLAN-gameplay-v2 §3b). At most one row
      per round carries it; absent on every older server build. */
  stretch?: boolean;
}

/** POST /api/game/session → the parts Phase 1 added. */
export interface GameResumeState {
  sessionId: string;
  roundIndex: number;
  bankCents: number;
  hardMode: boolean;
}

/** POST /api/game/round-complete result. */
export interface RoundCompleteResult {
  xpAwarded: number;
  xpTotal: number;
  careerLevel: number;
  leveledUp: boolean;
  xpIntoLevel: number;
  xpToNextLevel: number;
  /** Streak milestone reward paid by THIS round (db/050) — a single round can bump the
      ET-day streak. Absent/null when nothing crossed. Same shape as api.ts StreakMilestoneReward. */
  streakMilestone?: {
    days: number;
    coinsCents: number;
    itemType: string | null;
    xpBoost: boolean;
    collectibleKey: string | null;
  } | null;
}

export interface RoundCompleteBody {
  sessionId: string;
  roundIndex: number;
  correctCount: number;
  answeredCount: number;
  bankCents: number;
}

/* --------------------------- DTO → client map --------------------------- */

/** Synthetic ids for server questions whose stable code isn't one of the seeded
    client families. Negative so they can never collide with a bundled pack id
    (1..3xxx) or trip the `id >= 3001` hard-pack rules. */
let syntheticSeq = 0;
const syntheticIds = new Map<string, number>();

/** Reset the synthetic-id table (test seam; also called when a game starts so a
    long-lived PWA session can't grow the map without bound). */
export function resetSyntheticIds(): void {
  syntheticIds.clear();
  syntheticSeq = 0;
}

/** The client id for a server stable code. The three seeded families map back to
    their real bundled ids (so hard-pack value/no-repeat rules keep working);
    anything else (new server-authored content) gets a stable synthetic id. PURE
    apart from the memo table. */
export function idForStableCode(code: string): number {
  const proto = /^proto-(\d+)$/.exec(code);
  if (proto) return parseInt(proto[1], 10);
  const leo = /^leo14-(\d+)$/.exec(code);
  if (leo) return 2000 + parseInt(leo[1], 10);
  const hard = /^hard3-(\d+)$/.exec(code);
  if (hard) return 3000 + parseInt(hard[1], 10);
  const memo = syntheticIds.get(code);
  if (memo !== undefined) return memo;
  const id = -(++syntheticSeq);
  syntheticIds.set(code, id);
  return id;
}

/** The stable code for any question (server-served or bundled). */
export function questionCode(q: { id: number; stableCode?: string }): string {
  return q.stableCode || stableCodeFor(q.id);
}

/** DB level (0..3) → client hardness. 0 → undefined (plain easy, the engine's
    default), 1/2/3 → 1/2/3. Anything out of range degrades to undefined. */
export function hardnessForLevel(level: unknown): 1 | 2 | 3 | undefined {
  if (level === 3) return 3;
  if (level === 2) return 2;
  if (level === 1) return 1;
  return undefined;
}

/** Map one server DTO onto the engine's Question. Reading delay is deliberately
    left unset: engine.readingDelayMs() then applies the client's body-length rule
    (<80 chars standard · 80–130 +1s · >130 +2s), identical to bundled questions.
    Level-3 questions carry the richer hard-mode starting value, matching the
    bundled hard pack. Returns null for a malformed row (never throws). */
export function mapServedQuestion(dto: ServedQuestionDto | null | undefined): Question | null {
  if (!dto || typeof dto !== 'object') return null;
  const code = typeof dto.stableCode === 'string' ? dto.stableCode.trim() : '';
  if (!code) return null;
  if (typeof dto.body !== 'string' || dto.body.length === 0) return null;
  if (!Array.isArray(dto.options) || dto.options.length < 2) return null;
  if (!dto.options.every((o) => typeof o === 'string')) return null;
  const ci = dto.correctIndex;
  if (typeof ci !== 'number' || !Number.isInteger(ci) || ci < 0 || ci >= dto.options.length) return null;
  const hardness = hardnessForLevel(dto.level);
  const q: Question = {
    id: idForStableCode(code),
    stableCode: code,
    category: typeof dto.category === 'string' && dto.category ? dto.category : 'Catholic Trivia',
    body: dto.body,
    options: dto.options.slice(),
    correctIndex: ci,
    citation: typeof dto.citation === 'string' && dto.citation ? dto.citation : '',
  };
  if (hardness) q.hardness = hardness;
  if (hardness === 3) q.valueCents = HARD_START_CENTS;
  // ⭐ Stretch: only `true` counts (an older build simply omits the field). The
  // engine applies the ×1.5 value and the no-risk clamp from this one flag.
  if (dto.stretch === true) q.stretch = true;
  return q;
}

/** Map a served batch, dropping malformed rows. PURE. */
export function mapRoundQuestions(rows: readonly ServedQuestionDto[] | null | undefined): Question[] {
  if (!Array.isArray(rows)) return [];
  const out: Question[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const q = mapServedQuestion(row);
    if (!q || !q.stableCode) continue;
    if (seen.has(q.stableCode)) continue; // a server hiccup must not repeat inside one round
    seen.add(q.stableCode);
    out.push(q);
  }
  return out;
}

/* ------------------------------ normalizers ------------------------------ */

/** Validate the `resume` block of POST /api/game/session (or the body of
    GET /api/game/session/active). Anything off-shape → null (start fresh). PURE. */
export function normalizeResume(raw: unknown, totalRounds: number): GameResumeState | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const sessionId = r.sessionId;
  const roundIndex = r.roundIndex;
  const bankCents = r.bankCents;
  if (typeof sessionId !== 'string' || !sessionId) return null;
  if (typeof roundIndex !== 'number' || !Number.isInteger(roundIndex)) return null;
  // A resume only ever exists BETWEEN rounds: round 1 done .. one short of the end.
  if (roundIndex < 1 || roundIndex >= totalRounds) return null;
  if (typeof bankCents !== 'number' || !Number.isFinite(bankCents)) return null;
  return { sessionId, roundIndex, bankCents: Math.round(bankCents), hardMode: r.hardMode === true };
}

/** Non-negative integer or 0. */
function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : 0;
}

/** Tolerant read of the round-complete payload (a server that predates a field
    simply yields 0/false rather than breaking the XP beat). PURE. */
export function normalizeRoundComplete(raw: unknown): RoundCompleteResult {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const out: RoundCompleteResult = {
    xpAwarded: num(r.xpAwarded),
    xpTotal: num(r.xpTotal),
    careerLevel: num(r.careerLevel),
    leveledUp: r.leveledUp === true,
    xpIntoLevel: num(r.xpIntoLevel),
    xpToNextLevel: num(r.xpToNextLevel),
  };
  // Only present when the server paid one (db/050) — keeps the legacy shape byte-identical.
  const sm = normalizeStreakMilestone(r.streakMilestone);
  if (sm) out.streakMilestone = sm;
  return out;
}

/** Pass the server's StreakMilestoneDto through with safe types; null when absent/malformed. */
export function normalizeStreakMilestone(raw: unknown): RoundCompleteResult['streakMilestone'] {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Record<string, unknown>;
  const days = num(m.days);
  if (!(days > 0)) return null;
  return {
    days,
    coinsCents: num(m.coinsCents),
    itemType: typeof m.itemType === 'string' && m.itemType ? m.itemType : null,
    xpBoost: m.xpBoost === true,
    collectibleKey: typeof m.collectibleKey === 'string' && m.collectibleKey ? m.collectibleKey : null,
  };
}

/** Career level from the session response; 0 = unknown (server predates Phase 1). */
export function normalizeCareerLevel(raw: unknown): number {
  const n = num(raw);
  return n > 0 ? n : 0;
}

/* --------------------------- round question load --------------------------- */

export interface LoadRoundDeps {
  /** True only for a signed-in player. Guests never reach the server path. */
  isAuthed(): boolean;
  /** The open server session id, or null when none was opened. */
  sessionId(): string | null;
  /** POST /api/game/round-questions (injected — keeps api.ts out of this module).
      `groupKey` is the round picker's category choice; null = Mixed (today's
      draw). The server ignores unknown keys, so no pre-validation here. */
  fetchQuestions(body: {
    sessionId: string;
    roundIndex: number;
    count: number;
    hardMode: boolean;
    groupKey?: string | null;
  }): Promise<ApiResult<{ questions: ServedQuestionDto[] }>>;
  /** The bundled-pack questions for this round — the fallback at every rung. */
  fallback(): Question[];
  /** Give up on the server after this long and play the bundled pack. */
  timeoutMs?: number;
  /** Optional observability hook: which rung of the ladder was used. */
  onSource?(source: 'server' | 'fallback', reason: string): void;
}

const DEFAULT_ROUND_TIMEOUT_MS = 2_500;

/** Resolve `p` or, after `ms`, the `onTimeout` value. Never rejects. */
export async function withTimeout<T>(p: Promise<T>, ms: number, onTimeout: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const guard = new Promise<T>((resolve) => {
    timer = setTimeout(() => resolve(onTimeout), ms);
  });
  try {
    return await Promise.race([p.catch(() => onTimeout), guard]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * The round's questions: server-served when possible, bundled pack otherwise.
 * Never throws and never returns an empty list (the fallback is the floor).
 * `groupKey` (the round picker's choice) only shapes the SERVER draw — the
 * fallback ladder ignores it entirely, so a failed server draw after a pick
 * falls back exactly as today.
 */
export async function loadRoundQuestions(
  deps: LoadRoundDeps,
  opts: { roundIndex: number; count: number; hardMode: boolean; groupKey?: string | null },
): Promise<Question[]> {
  const fallback = (reason: string): Question[] => {
    deps.onSource?.('fallback', reason);
    return deps.fallback();
  };
  if (!deps.isAuthed()) return fallback('guest');
  const sessionId = deps.sessionId();
  if (!sessionId) return fallback('no-session');

  const timeoutMs = deps.timeoutMs ?? DEFAULT_ROUND_TIMEOUT_MS;
  const timedOut: ApiResult<{ questions: ServedQuestionDto[] }> = {
    ok: false,
    offline: true,
    error: 'round-questions timed out',
  };
  const res = await withTimeout(
    deps.fetchQuestions({
      sessionId,
      roundIndex: opts.roundIndex,
      count: opts.count,
      hardMode: opts.hardMode,
      groupKey: opts.groupKey ?? null,
    }),
    timeoutMs,
    timedOut,
  );
  if (!res.ok) return fallback(res.offline ? 'offline' : 'http-' + (res.status ?? 0));
  const mapped = mapRoundQuestions(res.data?.questions);
  if (mapped.length === 0) return fallback('empty');
  deps.onSource?.('server', 'served-' + mapped.length);
  return mapped;
}

/* ------------------------ round save + XP celebration ------------------------ */

export interface XpTallyOpts {
  xpAwarded: number;
  xpIntoLevel: number;
  xpToNextLevel: number;
  level: number;
  leveledUp: boolean;
}

export interface RoundSaveDeps {
  /** POST /api/game/round-complete (injected). */
  post(body: RoundCompleteBody): Promise<ApiResult<RoundCompleteResult>>;
  /** Agent C's XP tally overlay — resolves when the animation/dismiss completes. */
  showXpTally(opts: XpTallyOpts): Promise<void>;
  /** Agent C's level-up moment. */
  showLevelUp(opts: { level: number }): Promise<void>;
  /** Told the (possibly new) career level so the next round intro can show it. */
  onCareerLevel?(level: number): void;
}

/**
 * Save the finished round server-side and play the XP beats. A failed save is
 * silent (the round still counts locally and the game moves on) — never block
 * play on the network. UI beats are awaited so the next screen doesn't race them,
 * but a throwing/absent overlay can't wedge the game either.
 */
export async function saveRoundAndCelebrate(
  deps: RoundSaveDeps,
  body: RoundCompleteBody,
): Promise<RoundCompleteResult | null> {
  let res: ApiResult<RoundCompleteResult>;
  try {
    res = await deps.post(body);
  } catch {
    return null;
  }
  if (!res.ok) return null;
  const r = normalizeRoundComplete(res.data);
  if (r.careerLevel > 0) deps.onCareerLevel?.(r.careerLevel);
  try {
    await deps.showXpTally({
      xpAwarded: r.xpAwarded,
      xpIntoLevel: r.xpIntoLevel,
      xpToNextLevel: r.xpToNextLevel,
      level: r.careerLevel,
      leveledUp: r.leveledUp,
    });
    if (r.leveledUp) await deps.showLevelUp({ level: r.careerLevel });
  } catch {
    /* an overlay that fails must never strand the player between rounds */
  }
  return r;
}
